import React, { useState, useEffect } from "react";
import { Table, Button, notification, Spin, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiRequest } from "util/services";
import styles from "./style.module.scss";

const HotTermsBlacklist = () => {
  const [blacklistedTerms, setBlacklistedTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const user = useSelector((state) => state.userReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const { t } = useTranslation();

  useEffect(() => {
    fetchBlacklistedTerms();
  }, []);

  useEffect(() => {
    setFilteredTerms(blacklistedTerms);
  }, [blacklistedTerms]);

  const fetchBlacklistedTerms = async () => {
    setLoading(true);
    try {
      const result = await apiRequest("users/hotterms/blacklist/get", "POST", {
        user_id: user?.uid,
      }, true);

      if (result.success) {
        setBlacklistedTerms(result.success || []);
        setFilteredTerms(result.success || []);
      } else {
        notification.error({
          message: result.error || t("dashboard_tooltips.hotterms.blacklist.fetch_error", "Failed to fetch blacklisted terms"),
        });
      }
    } catch (error) {
      notification.error({
        message: t("dashboard_tooltips.hotterms.blacklist.fetch_error", "Failed to fetch blacklisted terms"),
      });
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = async () => {
    if (!termToDelete) return;
    
    setDeleting(true);
    try {
      const result = await apiRequest("users/hotterms/blacklist/delete", "POST", {
        term_id: termToDelete.id,
        user_id: user?.uid,
      }, true);

      if (result.success) {
        notification.success({
          message: t("dashboard_tooltips.hotterms.blacklist.delete_success", "Term deleted"),
        });
        setShowDeleteModal(false);
        setTermToDelete(null);
        fetchBlacklistedTerms();
      } else {
        notification.error({
          message: result.error || t("dashboard_tooltips.hotterms.blacklist.delete_error", "Failed to delete term"),
        });
      }
    } catch (error) {
      notification.error({
        message: t("dashboard_tooltips.hotterms.blacklist.delete_error", "Failed to delete term"),
      });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: t("dashboard_tooltips.hotterms.blacklist.table.term", "Term"),
      dataIndex: "term",
      key: "term",
      render: (text) => <span className={styles.termText}>{text}</span>,
    },
    {
      title: t("dashboard_tooltips.hotterms.blacklist.table.date_added", "Date Added"),
      dataIndex: "date_added",
      key: "date_added",
      render: (date) => (
        <span className={styles.dateText}>
          {new Date(date).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: t("dashboard_tooltips.hotterms.blacklist.table.blacklisted_by", "Blacklisted By"),
      dataIndex: "blacklisted_by",
      key: "blacklisted_by",
      render: (text) => <span className={styles.userText}>{text}</span>,
    },
    {
      title: t("dashboard_tooltips.hotterms.blacklist.table.actions", "Actions"),
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            setTermToDelete(record);
            setShowDeleteModal(true);
          }}
          title={t("dashboard_tooltips.hotterms.blacklist.delete", "Delete term")}
        >
          {t("common.delete", "Delete")}
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.blacklistContainer}>
      <div className={styles.blacklistHeader}>
        <h3 className={styles.blacklistTitle}>
          {t("dashboard_tooltips.hotterms.blacklist.title", "Hot Term Blacklist Management")}
        </h3>
      </div>

      <div className={styles.tableContainer}>
        {filteredTerms.length > 0 ? (
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={filteredTerms}
              rowKey="id"
              className={styles.blacklistTable}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              locale={{
                emptyText: t("dashboard_tooltips.hotterms.blacklist.empty", "No blacklisted terms found"),
              }}
            />
          </Spin>
        ) : (
          <div className={styles.emptyState}>
            <p>{t("dashboard_tooltips.hotterms.blacklist.no_terms", "No blacklisted hotterms")}</p>
          </div>
        )}
      </div>

      <Modal
        title={t("dashboard_tooltips.hotterms.blacklist.delete_confirm_title", "Permanently Delete Term")}
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setTermToDelete(null);
        }}
        confirmLoading={deleting}
        okText={t("common.delete", "Delete")}
        cancelText={t("common.cancel", "Cancel")}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t(
            "dashboard_tooltips.hotterms.blacklist.delete_confirm_message",
            "Are you sure you want to permanently delete the term '{{term}}'? This action cannot be undone.",
            { term: termToDelete?.term }
          )}
        </p>
      </Modal>
    </div>
  );
};

export default HotTermsBlacklist; 